import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from "@mui/material";

const colors = {
  primary: "#1565C0",
  dark: "#0D47A1",
  pastel: "#C4DCF7",
  light: "#EAF2FD",
  border: "#D5E1EF",
  text: "#172033",
  muted: "#64748B",
};

const money = (value) => {
  return new Intl.NumberFormat("es-EC", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
};

const parseDate = (raw) => {
  if (!raw) {
    return null;
  }

  const value = String(raw);

  if (/^\d{4}-\d{2}$/.test(value)) {
    return new Date(`${value}-01T00:00:00`);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }

  return new Date(value);
};

const getCurrentMonthValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
};

const getMonthOptions = () => {
  const now = new Date();
  const options = [];

  for (let index = 0; index <= 12; index += 1) {
    const date = new Date(
      now.getFullYear(),
      now.getMonth() - index,
      1
    );

    const value = `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}`;

    const rawLabel = date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });

    options.push({
      value,
      label:
        rawLabel.charAt(0).toUpperCase() +
        rawLabel.slice(1),
    });
  }

  return options;
};

const formatLabel = (item, periodo) => {
  const raw = item?.fecha || item?.mes;
  const date = parseDate(raw);

  if (!date || Number.isNaN(date.getTime())) {
    return String(raw || "");
  }

  if (periodo === "anio") {
    return date.toLocaleDateString("es-EC", {
      month: "short",
    });
  }

  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
  });
};

const formatTooltipDate = (item, periodo) => {
  const raw = item?.fecha || item?.mes;
  const date = parseDate(raw);

  if (!date || Number.isNaN(date.getTime())) {
    return String(raw || "");
  }

  if (periodo === "anio") {
    return date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });
  }

  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getTitle = (periodo, selectedMonth) => {
  if (periodo === "semana") {
    return "Ventas de la semana";
  }

  if (periodo === "mes") {
    const [year, month] = selectedMonth.split("-");
    const date = new Date(
      Number(year),
      Number(month) - 1,
      1
    );

    const rawLabel = date.toLocaleDateString("es-EC", {
      month: "long",
      year: "numeric",
    });

    return (
      rawLabel.charAt(0).toUpperCase() +
      rawLabel.slice(1)
    );
  }

  return "Ventas del año";
};

const getLabelStep = (length, periodo) => {
  if (periodo === "semana" || periodo === "anio") {
    return 1;
  }

  if (length <= 10) {
    return 1;
  }

  if (length <= 20) {
    return 2;
  }

  return 3;
};

const SalesChartPanel = ({ accessToken }) => {
  const [periodo, setPeriodo] = useState("semana");
  const [selectedMonth, setSelectedMonth] = useState(
    getCurrentMonthValue()
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  const monthOptions = useMemo(
    () => getMonthOptions(),
    []
  );

  useEffect(() => {
    const loadData = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setHovered(null);

      try {
        const url =
          periodo === "mes"
            ? `/dashboard/ventas/?periodo=mes&mes=${selectedMonth}`
            : `/dashboard/ventas/?periodo=${periodo}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await response.json();

        setItems(
          response.ok && Array.isArray(data)
            ? data
            : []
        );
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [periodo, selectedMonth, accessToken]);

  const hasData = useMemo(() => {
    return items.some(
      (item) => Number(item.neto || 0) !== 0
    );
  }, [items]);

  const total = useMemo(() => {
    return items.reduce(
      (accumulator, item) =>
        accumulator + Number(item.neto || 0),
      0
    );
  }, [items]);

  const chart = useMemo(() => {
    const values = items.map((item) =>
      Number(item.neto || 0)
    );

    const width = 760;
    const height = 280;
    const paddingLeft = 64;
    const paddingRight = 24;
    const paddingTop = 26;
    const paddingBottom = 38;
    const innerWidth =
      width - paddingLeft - paddingRight;
    const innerHeight =
      height - paddingTop - paddingBottom;

    const maxValue = Math.max(0, ...values);
    const minValue = Math.min(0, ...values);
    const range = maxValue - minValue || 1;

    const getY = (value) => {
      return (
        paddingTop +
        ((maxValue - value) / range) *
          innerHeight
      );
    };

    const zeroY = getY(0);

    const points = values.map((value, index) => {
      const x =
        values.length === 1
          ? paddingLeft + innerWidth / 2
          : paddingLeft +
            (index * innerWidth) /
              (values.length - 1);

      return {
        x,
        y: getY(value),
        value,
      };
    });

    const polyline = points
      .map((point) => `${point.x},${point.y}`)
      .join(" ");

    const area =
      points.length > 0
        ? `${points
            .map(
              (point) =>
                `${point.x},${point.y}`
            )
            .join(" ")} ${
            points[points.length - 1].x
          },${zeroY} ${points[0].x},${zeroY}`
        : "";

    const grid = Array.from(
      { length: 4 },
      (_, index) => {
        const ratio = index / 3;
        const value = maxValue - ratio * range;

        return {
          value,
          y: getY(value),
        };
      }
    );

    return {
      width,
      height,
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom,
      points,
      polyline,
      area,
      grid,
      zeroY,
      labelStep: getLabelStep(
        values.length,
        periodo
      ),
    };
  }, [items, periodo]);

  if (!loading && !hasData) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.75, sm: 2.5 },
        borderRadius: 2.5,
        bgcolor: "#fff",
        borderColor: colors.border,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: colors.dark,
              fontSize: 17,
              fontWeight: 800,
            }}
          >
            {getTitle(periodo, selectedMonth)}
          </Typography>

          {!loading && (
            <Typography
              sx={{
                color: colors.muted,
                fontSize: 12.5,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Total: {money(total)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: {
              xs: "100%",
              md: "auto",
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: {
                xs: "100%",
                sm: 145,
              },
            }}
          >
            <Select
              value={periodo}
              onChange={(event) =>
                setPeriodo(event.target.value)
              }
              sx={{
                borderRadius: 2,
                color: colors.dark,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <MenuItem value="semana">
                Semana
              </MenuItem>

              <MenuItem value="mes">
                Mes
              </MenuItem>

              <MenuItem value="anio">
                Año
              </MenuItem>
            </Select>
          </FormControl>

          {periodo === "mes" && (
            <FormControl
              size="small"
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 185,
                },
              }}
            >
              <Select
                value={selectedMonth}
                onChange={(event) =>
                  setSelectedMonth(
                    event.target.value
                  )
                }
                sx={{
                  borderRadius: 2,
                  color: colors.dark,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {monthOptions.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress
            size={30}
            sx={{ color: colors.primary }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Box
            sx={{
              minWidth: 640,
              position: "relative",
            }}
          >
            <svg
              viewBox={`0 0 ${chart.width} ${chart.height}`}
              style={{
                width: "100%",
                height: 310,
                display: "block",
              }}
              onMouseLeave={() =>
                setHovered(null)
              }
            >
              {chart.grid.map((line, index) => (
                <g key={index}>
                  <line
                    x1={chart.paddingLeft}
                    x2={
                      chart.width -
                      chart.paddingRight
                    }
                    y1={line.y}
                    y2={line.y}
                    stroke={colors.border}
                    strokeWidth="1"
                  />

                  <text
                    x="4"
                    y={line.y + 4}
                    fontSize="11"
                    fill={colors.muted}
                    fontWeight="500"
                  >
                    {money(line.value)}
                  </text>
                </g>
              ))}

              {chart.zeroY >= chart.paddingTop &&
                chart.zeroY <=
                  chart.height -
                    chart.paddingBottom && (
                  <line
                    x1={chart.paddingLeft}
                    x2={
                      chart.width -
                      chart.paddingRight
                    }
                    y1={chart.zeroY}
                    y2={chart.zeroY}
                    stroke="#AFC5DF"
                    strokeWidth="1.5"
                  />
                )}

              {chart.area && (
                <polygon
                  points={chart.area}
                  fill={colors.light}
                />
              )}

              {chart.polyline && (
                <polyline
                  points={chart.polyline}
                  fill="none"
                  stroke={colors.primary}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {chart.points.map((point, index) => {
                const showLabel =
                  index === 0 ||
                  index === chart.points.length - 1 ||
                  index % chart.labelStep === 0;

                return (
                  <g key={index}>
                    <line
                      x1={point.x}
                      x2={point.x}
                      y1={chart.paddingTop}
                      y2={
                        chart.height -
                        chart.paddingBottom
                      }
                      stroke={
                        hovered === index
                          ? "#B8C8DB"
                          : "transparent"
                      }
                      strokeWidth="1"
                    />

                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={hovered === index ? 6 : 4}
                      fill={
                        hovered === index
                          ? colors.dark
                          : colors.pastel
                      }
                      stroke={colors.primary}
                      strokeWidth="2"
                    />

                    <circle
                      cx={point.x}
                      cy={point.y}
                      r="18"
                      fill="transparent"
                      style={{ cursor: "pointer" }}
                      onMouseEnter={() =>
                        setHovered(index)
                      }
                      onMouseMove={() =>
                        setHovered(index)
                      }
                    />

                    {showLabel && (
                      <text
                        x={point.x}
                        y={chart.height - 8}
                        textAnchor="middle"
                        fontSize="11"
                        fill={colors.muted}
                        fontWeight="600"
                      >
                        {formatLabel(
                          items[index],
                          periodo
                        )}
                      </text>
                    )}
                  </g>
                );
              })}

              {hovered !== null &&
                items[hovered] &&
                chart.points[hovered] && (
                  <g>
                    <rect
                      x={
                        chart.points[hovered].x >
                        chart.width - 205
                          ? chart.points[hovered].x - 190
                          : chart.points[hovered].x + 12
                      }
                      y={
                        chart.points[hovered].y < 70
                          ? chart.points[hovered].y + 12
                          : chart.points[hovered].y - 63
                      }
                      width="178"
                      height="52"
                      rx="7"
                      fill={colors.dark}
                    />

                    <text
                      x={
                        chart.points[hovered].x >
                        chart.width - 205
                          ? chart.points[hovered].x - 178
                          : chart.points[hovered].x + 24
                      }
                      y={
                        chart.points[hovered].y < 70
                          ? chart.points[hovered].y + 32
                          : chart.points[hovered].y - 43
                      }
                      fontSize="11"
                      fill="#DCE8F7"
                      fontWeight="500"
                    >
                      {formatTooltipDate(
                        items[hovered],
                        periodo
                      )}
                    </text>

                    <text
                      x={
                        chart.points[hovered].x >
                        chart.width - 205
                          ? chart.points[hovered].x - 178
                          : chart.points[hovered].x + 24
                      }
                      y={
                        chart.points[hovered].y < 70
                          ? chart.points[hovered].y + 50
                          : chart.points[hovered].y - 25
                      }
                      fontSize="14"
                      fill="#fff"
                      fontWeight="700"
                    >
                      {money(items[hovered].neto)}
                    </text>
                  </g>
                )}
            </svg>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default SalesChartPanel;